import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Warning, ShieldWarning, Check, ArrowCounterClockwise } from "@phosphor-icons/react";

/**
 * SuspiciousTrucksCard — Phase D companion. Surfaces truck rows whose
 * names look like test/spam/abandoned data so the admin can clean them
 * up BEFORE the new "truck goes live" push notification announces them
 * to the entire opted-in audience.
 *
 * Flags returned by the backend (each row may have multiple):
 *   - empty            : truck name is blank
 *   - very_long        : > 60 chars
 *   - all_caps         : entire name is uppercase
 *   - test_pattern     : matches "test" / "asdf" / "delete me" / etc.
 *   - repeated_chars   : 5+ of the same char in a row
 *   - non_printable    : non-ASCII / control chars
 *   - low_alpha_ratio  : <30% letters (mostly numbers/symbols)
 *   - very_short       : ≤ 2 chars
 */
const FLAG_LABELS = {
  empty: { label: "Empty", color: "rose" },
  very_long: { label: "Very long", color: "amber" },
  all_caps: { label: "ALL CAPS", color: "amber" },
  test_pattern: { label: "Test/placeholder", color: "rose" },
  repeated_chars: { label: "Repeated chars", color: "amber" },
  non_printable: { label: "Non-printable", color: "rose" },
  low_alpha_ratio: { label: "Mostly symbols", color: "amber" },
  very_short: { label: "Very short", color: "amber" },
};

export default function SuspiciousTrucksCard() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  // Track approved rows so the user can undo without a refetch. Each
  // entry is the row payload at approval time; we keep the visual row
  // around as a faded "Approved · Undo" stub instead of yanking it
  // from the DOM, which feels jumpy when the admin is mid-triage.
  const [approved, setApproved] = useState({}); // truck_id → row
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/admin/suspicious-trucks")
      .then(({ data }) => {
        if (!mounted) return;
        setItems(data.items || []);
      })
      .catch((e) => {
        if (!mounted) return;
        toast.error(formatApiError(e) || "Couldn't load suspicious trucks");
        setItems([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const onApprove = async (row) => {
    const tid = row.truck_id || row.id;
    setBusyId(tid);
    try {
      await api.post("/admin/suspicious-trucks/approve", {
        truck_id: tid,
        truck_name: row.truck_name || "",
      });
      setApproved((prev) => ({ ...prev, [tid]: row }));
      toast.success("Marked as reviewed");
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't approve");
    } finally {
      setBusyId(null);
    }
  };

  const onUndo = async (row) => {
    const tid = row.truck_id || row.id;
    setBusyId(tid);
    try {
      await api.post("/admin/suspicious-trucks/unapprove", {
        truck_id: tid,
        truck_name: row.truck_name || "",
      });
      setApproved((prev) => {
        const next = { ...prev };
        delete next[tid];
        return next;
      });
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't undo");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="admin-suspicious-trucks">
        <h2 className="font-display font-bold text-base">Suspicious truck names</h2>
        <p className="text-xs text-muted-foreground mt-1">Loading…</p>
      </section>
    );
  }

  const totalRaw = items?.length || 0;
  const approvedCount = Object.keys(approved).length;
  const total = Math.max(0, totalRaw - approvedCount);

  return (
    <section
      className={`rounded-2xl border ${total > 0 ? "border-amber-500/40 bg-amber-500/5" : totalRaw > 0 ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card"} p-5 space-y-3`}
      data-testid="admin-suspicious-trucks"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-base inline-flex items-center gap-2">
            {total > 0 ? (
              <Warning weight="duotone" className="h-5 w-5 text-amber-600" />
            ) : (
              <ShieldWarning weight="duotone" className="h-5 w-5 text-emerald-600" />
            )}
            Suspicious truck names
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {totalRaw === 0
              ? "No flagged truck names. Public landing card + Phase D pushes are clean."
              : total === 0
                ? `All ${totalRaw} flagged ${totalRaw === 1 ? "truck has" : "trucks have"} been reviewed this session.`
                : `${total} truck${total === 1 ? "" : "s"} flagged for admin review${
                    approvedCount > 0 ? ` · ${approvedCount} reviewed` : ""
                  }.`}
          </p>
        </div>
        {totalRaw > 0 && (
          <button
            type="button"
            onClick={() => setOpen((s) => !s)}
            className="h-9 px-3 rounded-full border border-border bg-card text-xs font-semibold hover:bg-muted"
            data-testid="suspicious-trucks-toggle"
          >
            {open ? "Hide" : "Review"}
          </button>
        )}
      </header>

      {open && totalRaw > 0 && (
        <div className="space-y-2" data-testid="suspicious-trucks-list">
          {items.map((row) => {
            const tid = row.truck_id || row.id;
            const isApproved = !!approved[tid];
            const isBusy = busyId === tid;
            return (
              <div
                key={`${row.source}-${tid}`}
                className={`rounded-xl border bg-card p-3 transition-opacity ${
                  isApproved ? "border-emerald-500/30 bg-emerald-500/5 opacity-70" : "border-border"
                }`}
                data-testid={`suspicious-truck-row-${tid}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm font-semibold break-all">
                      {row.truck_name || <span className="italic text-muted-foreground">(empty)</span>}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {row.owner_email
                        ? <>Owner: <span className="font-semibold">{row.owner_email}</span> · {row.owner_role || "—"}</>
                        : <span className="italic">No owner record</span>}
                      {row.address ? <> · {row.address}</> : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 items-start">
                    {row.flags.map((f) => {
                      const meta = FLAG_LABELS[f] || { label: f, color: "amber" };
                      return (
                        <span
                          key={f}
                          className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 border ${
                            meta.color === "rose"
                              ? "bg-rose-500/10 border-rose-500/40 text-rose-700 dark:text-rose-300"
                              : "bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-300"
                          }`}
                        >
                          {meta.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-border/50 flex items-center justify-end gap-2">
                  {isApproved ? (
                    <>
                      <span className="text-[11px] text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1">
                        <Check weight="bold" className="h-3 w-3" />
                        Marked as reviewed
                      </span>
                      <button
                        type="button"
                        onClick={() => onUndo(row)}
                        disabled={isBusy}
                        className="h-7 px-2 rounded-full text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1"
                        data-testid={`suspicious-truck-undo-${tid}`}
                      >
                        <ArrowCounterClockwise weight="bold" className="h-3 w-3" />
                        Undo
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onApprove(row)}
                      disabled={isBusy}
                      className="h-8 px-3 rounded-full bg-card border border-border text-xs font-semibold hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1.5"
                      data-testid={`suspicious-truck-approve-${tid}`}
                      title="Mark this name as admin-reviewed. It'll come back if the owner edits it later."
                    >
                      <Check weight="bold" className="h-3.5 w-3.5 text-emerald-600" />
                      Looks fine
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
